import { CheckCircle2 } from 'lucide-react';

interface YourGenieYourWayProps {
  /** Optional market slug for market-specific imagery */
  market?: string;
  /** Optional subtitle override */
  subtitle?: string;
}

/**
 * Build image paths for the YGYW section.
 *
 * If a market is provided, try the market-specific image first; if it 404s
 * the <img onError> handler swaps to the generic fallback at runtime.
 * No hardcoded market list to maintain — adding a new market just means
 * dropping its `genie-*.webp` files into `public/solutions-pages/{market}/`.
 */
const getImagePaths = (featureId: string, market?: string) => {
  const imageName = `genie-${featureId.replace(/_/g, '-')}`;
  const fallback = `/optimized/new-imagery/${imageName}.webp`;
  const primary = market ? `/solutions-pages/${market}/${imageName}.webp` : fallback;
  return { primary, fallback };
};

const GENIE_FEATURES = [
  {
    id: 'customize_voice',
    question: 'Can I customize how my Genie speaks and acts?',
    answer: 'Absolutely. Your Genie is yours to shape.',
    bullets: [
      'Choose from dozens of natural-sounding voices',
      'Set the tone—professional, friendly, or somewhere in between',
      'Define personality traits that match your brand',
      'Customize greetings and conversation style',
    ],
    image: '/optimized/new-imagery/genie-customize-voice.webp',
  },
  {
    id: 'knowledge_base',
    question: 'How do I add knowledge and keep it updated?',
    answer: 'Simple. Upload documents, paste text, or connect your existing content.',
    bullets: [
      'Upload PDFs, docs, or paste text directly',
      'Your Genie learns your business instantly',
      'Update anytime—changes apply immediately',
      'No technical skills required',
    ],
    image: '/optimized/new-imagery/genie-knowledge-base.webp',
  },
  {
    id: 'access_leads',
    question: 'How do I access my lead information?',
    answer: 'Your way. Dashboard, email, SMS—you choose.',
    bullets: [
      'Real-time dashboard with all conversation details',
      'Get instant SMS alerts for new leads',
      'Email summaries with full transcripts',
      'Export data anytime you need it',
    ],
    image: '/optimized/new-imagery/genie-access-leads.webp',
  },
  {
    id: 'transfer_human',
    question: 'Can I transfer calls to a human?',
    answer: 'Yes. Set your rules, and your Genie follows them.',
    bullets: [
      'Define what triggers a transfer',
      'Route to specific team members or departments',
      'Set up on-call schedules',
      'Warm handoff with context provided',
    ],
    image: '/optimized/new-imagery/genie-transfer-human.webp',
  },
  {
    id: 'existing_number',
    question: 'Can I use my existing business phone number?',
    answer: 'Yes. Your customers call the same number they always have.',
    bullets: [
      'Seamless integration with your current phone system',
      'No need to change your business cards or website',
      'Works with most phone providers',
      'Simple forwarding setup',
    ],
    image: '/optimized/new-imagery/genie-existing-number.webp',
  },
  {
    id: 'new_number',
    question: 'Can I create a new phone number?',
    answer: 'Easily. Pick your area code and you\'re ready to go.',
    bullets: [
      'Choose from thousands of available numbers',
      'Local or toll-free options',
      'Active in minutes',
      'Perfect for new campaigns or locations',
    ],
    image: '/optimized/new-imagery/genie-new-number.webp',
  },
  {
    id: 'more_present',
    question: 'Can I be more present for my customers?',
    answer: 'Absolutely. Be everywhere your customers are.',
    bullets: [
      'Generate QR codes that connect straight to your Genie',
      'Create stickers for storefronts, vehicles, or handouts',
      'Embed links in emails and signatures',
      'Place your Genie anywhere your customers might need help',
    ],
    image: '/optimized/new-imagery/genie-more-present.webp',
  },
];

export const YourGenieYourWay = ({ market, subtitle }: YourGenieYourWayProps) => {
  // Build features with market-specific images + generic fallback
  const features = GENIE_FEATURES.map(feature => {
    const { primary, fallback } = getImagePaths(feature.id, market);
    return { ...feature, image: primary, fallbackImage: fallback };
  });

  return (
    <section className="w-full bg-white py-16 md:py-24 lg:py-28">
      {/* Header */}
      <div className="pb-8 md:pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
            Your Genie, Your Way
          </h2>
          <p className="text-lg md:text-xl lg:text-2xl text-gray-600 max-w-3xl mx-auto">
            {subtitle ?? 'Everything you need to know about making After Hours work exactly how you want it.'}
          </p>
        </div>
      </div>

      {/* Alternating feature rows */}
      {features.map((feature, index) => {
        const isEven = index % 2 === 0;

        return (
          <div key={feature.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
            <div className="flex flex-col lg:flex-row min-h-[auto] lg:min-h-[450px]">
              {/* Text content side */}
              <div className={`w-full lg:w-1/2 flex items-center justify-center py-6 sm:py-8 lg:py-10 px-4 sm:px-6 md:px-10 order-2 ${isEven ? 'lg:order-1 lg:justify-end' : 'lg:order-2 lg:justify-start'}`}>
                <div className={`w-full max-w-md lg:max-w-lg text-center lg:text-left ${isEven ? 'lg:mr-16 xl:mr-24' : 'lg:ml-16 xl:ml-24'}`}>
                  {/* Accent line */}
                  <div className="hidden lg:block w-12 h-1 bg-gradient-to-r from-purple-600 to-blue-600 mb-4" />

                  {/* Question as heading */}
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                    {feature.question}
                  </h3>

                  {/* Answer */}
                  <p className="text-lg sm:text-xl md:text-2xl text-purple-600 font-medium mb-5">
                    {feature.answer}
                  </p>

                  {/* Benefits list */}
                  <div className="space-y-2.5 text-left">
                    {feature.bullets.map((bullet, bulletIndex) => (
                      <div key={bulletIndex} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-base md:text-lg text-gray-600">{bullet}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Image side */}
              <div className={`w-full lg:w-1/2 flex items-center justify-center py-4 sm:py-6 lg:py-8 px-4 sm:px-6 lg:px-0 order-1 ${isEven ? 'lg:order-2 lg:justify-start lg:pr-10 xl:pr-16' : 'lg:order-1 lg:justify-end lg:pl-10 xl:pl-16'}`}>
                <div className="relative w-full max-w-xl lg:max-w-2xl h-[240px] sm:h-[300px] md:h-[340px] lg:h-[380px] overflow-hidden rounded-2xl shadow-xl">
                  <img
                    src={feature.image}
                    alt={feature.question}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const img = e.currentTarget;
                      if (img.src !== window.location.origin + feature.fallbackImage && !img.src.endsWith(feature.fallbackImage)) {
                        img.src = feature.fallbackImage;
                      }
                    }}
                  />
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 via-transparent to-blue-600/10 rounded-2xl" />
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </section>
  );
};
